import React, { useState, useEffect, useCallback } from 'react';
import { MESSAGE_TYPE, useFloatMessage } from './../../Providers/FloatMessage';
import { SubmitSuggestion, GetPublicConfig } from './../../../utilities/BL/Admin/Overlays/Playlist';
import { ConnectWebSocketForPlaylistAdmin, DisconnectWebSocketForPlaylistAdmin } from './../../../utilities/WS/Admin/PlaylistWS';

// ==============================================================================
// CONSTANTES Y CONFIGURACIÓN
// ==============================================================================
const SESSION_STORAGE_KEY = "playlist_fan_session_id";
const COOLDOWN_STORAGE_KEY = "playlist_next_allowed_ts";
const DEFAULT_COOLDOWN_MS = 2 * 60 * 1000; // 120 segundos (2 minutos) por defecto

// Utilidad: Formatea ms → "m:ss"
function msToMinSec(ms) {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Genera o recupera un fanSessionId único y persistente en el dispositivo del aficionado.
 */
function getOrCreateFanSessionId() {
  try {
    let sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) {
      sessionId = `fan_${Math.random().toString(36).substring(2, 9)}_${Date.now().toString(36)}`;
      localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }
    return sessionId;
  } catch (err) {
    return `fan_${Math.random().toString(36).substring(2, 9)}_${Date.now().toString(36)}`;
  }
}

/**
 * Vista móvil para el aficionado en el estadio ("Arma la playlist").
 * Ruta: /playlist
 */
const PlaylistFan = ({ cooldownMs = DEFAULT_COOLDOWN_MS }) => {
  const { showMessage } = useFloatMessage();

  // --- CONTROL DE VISTAS: 'intro' | 'form' | 'sent' | 'paused' ---
  const [view, setView] = useState("intro");

  // --- IDENTIDAD DE SESIÓN ---
  const [fanSessionId] = useState(getOrCreateFanSessionId);

  // --- CONFIGURACIÓN PÚBLICA DE LA DINÁMICA ---
  const [config, setConfig] = useState({
    isSubmissionActive: true,
    cooldownSeconds: 120,
    pausedMessage: '¡Gracias por participar! La playlist para este partido ha cerrado. ¡Disfruta el encuentro!',
    screenTitle: '¡Arma la Playlist!',
    screenSubtitle: '¿Qué canción o artista te gustaría escuchar en el estadio?'
  });

  // --- FORMULARIO Y SELECTOR DE INTENCIÓN (0: Canción, 1: Artista) ---
  const [suggestionType, setSuggestionType] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    artistName: '',
    fanName: ''
  });
  const [isSending, setIsSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [submissionResult, setSubmissionResult] = useState(null);

  // --- DETECCIÓN DE PARÁMETRO DE ORIGEN (?s=obs / ?s=screen) ---
  const [originSource, setOriginSource] = useState('web');

  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const source = searchParams.get('s') || searchParams.get('source') || 'web';
      setOriginSource(source);
    } catch (e) {
      setOriginSource('web');
    }
  }, []);

  // --- COOLDOWN ANTI-SPAM PERSISTENTE ---
  const [nextAllowedTs, setNextAllowedTs] = useState(null);
  const [remainingMs, setRemainingMs] = useState(0);

  // Cargar cooldown guardado al montar
  useEffect(() => {
    try {
      const saved = localStorage.getItem(COOLDOWN_STORAGE_KEY);
      if (saved) {
        const ts = parseInt(saved, 10);
        if (!Number.isNaN(ts) && ts > Date.now()) {
          setNextAllowedTs(ts);
        }
      }
    } catch (err) {
      console.error('[PlaylistFan] Error leyendo cooldown:', err);
    }
  }, []);

  // Timer del cooldown con tick cada 500ms
  useEffect(() => {
    const tick = () => {
      if (!nextAllowedTs) {
        setRemainingMs(0);
        return;
      }
      const diff = nextAllowedTs - Date.now();
      if (diff <= 0) {
        setRemainingMs(0);
        setNextAllowedTs(null);
        try {
          localStorage.removeItem(COOLDOWN_STORAGE_KEY);
        } catch {}
      } else {
        setRemainingMs(diff);
      }
    };

    tick();
    const timerId = setInterval(tick, 500);
    return () => clearInterval(timerId);
  }, [nextAllowedTs]);

  const canStartNew = !nextAllowedTs || Date.now() >= nextAllowedTs;

  // --- CONSULTA DE CONFIGURACIÓN PÚBLICA ---
  const fetchPublicConfig = useCallback(async () => {
    try {
      const res = await GetPublicConfig();
      if (res && res.success && res.data) {
        const data = res.data;
        setConfig(prev => ({
          ...prev,
          isSubmissionActive: data.isSubmissionActive !== undefined ? data.isSubmissionActive : true,
          cooldownSeconds: data.cooldownSeconds || 120,
          pausedMessage: data.pausedMessage || '¡Gracias por participar! La playlist para este partido ha cerrado. ¡Disfruta el encuentro!',
          screenTitle: data.screenTitle || '¡Arma la Playlist!',
          screenSubtitle: data.screenSubtitle || '¿Qué canción o artista te gustaría escuchar en el estadio?'
        }));

        if (data.isSubmissionActive === false) {
          setView(current => (current === 'sent' ? 'sent' : 'paused'));
        }
      }
    } catch (err) {
      console.error('[PlaylistFan] Error cargando configuración pública:', err);
    }
  }, []);

  useEffect(() => {
    fetchPublicConfig();
  }, [fetchPublicConfig]);

  // --- WEBSOCKET REAL-TIME LISTENER (settings_updated / toggle-pause) ---
  const handleWebSocketMessage = useCallback((packet) => {
    if (!packet || typeof packet !== 'object') return;

    const action = packet.action;
    const data = packet.data;

    if (action === 'settings_updated' || action === 'toggle-pause' || action === 'toggle_pause' || action === 'pause_toggled') {
      if (data) {
        setConfig(prev => {
          const updated = { ...prev, ...data };
          if (data.isSubmissionActive !== undefined) {
            updated.isSubmissionActive = data.isSubmissionActive;
            if (!data.isSubmissionActive) {
              setView(curr => (curr === 'sent' ? 'sent' : 'paused'));
            } else {
              setView(curr => (curr === 'paused' ? 'intro' : curr));
            }
          }
          return updated;
        });
      }
    } else if (action === 'dynamic_reset') {
      setSubmissionResult(null);
      setErrorMsg(null);
    }
  }, []);

  useEffect(() => {
    ConnectWebSocketForPlaylistAdmin(
      handleWebSocketMessage,
      () => {},
      'fan:playlist'
    );

    return () => {
      DisconnectWebSocketForPlaylistAdmin();
    };
  }, [handleWebSocketMessage]);

  // --- HANDLERS ---
  const handleStart = () => {
    if (!config.isSubmissionActive) {
      setView("paused");
      return;
    }
    if (!canStartNew) {
      showMessage({
        type: MESSAGE_TYPE.INFO,
        text: `Espera ${msToMinSec(remainingMs)} para enviar otra sugerencia.`
      });
      return;
    }
    setErrorMsg(null);
    setView("form");
  };

  const handleSelectorChange = (type) => {
    setSuggestionType(type);
    setErrorMsg(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Límites de caracteres
    if (name === 'title' && value.length > 100) return;
    if (name === 'artistName' && value.length > 80) return;
    if (name === 'fanName' && value.length > 50) return;

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    // Verificación de pausa
    if (!config.isSubmissionActive) {
      setErrorMsg(config.pausedMessage || "La dinámica se encuentra pausada en este momento.");
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: config.pausedMessage || "La dinámica se encuentra pausada."
      });
      return;
    }

    // Verificación de cooldown
    if (!canStartNew) {
      setErrorMsg(`Por favor espera ${msToMinSec(remainingMs)} antes de enviar otra sugerencia.`);
      showMessage({
        type: MESSAGE_TYPE.INFO,
        text: `Espera ${msToMinSec(remainingMs)} para enviar otra sugerencia.`
      });
      return;
    }

    // Validación visual rápida
    if (!formData.title.trim()) {
      const msg = suggestionType === 0
        ? "Por favor escribe el título de la canción."
        : "Por favor escribe el nombre del artista o grupo musical.";
      setErrorMsg(msg);
      return;
    }

    setIsSending(true);
    setErrorMsg(null);

    const payload = {
      suggestionType,
      title: formData.title.trim(),
      artistName: suggestionType === 0 ? (formData.artistName.trim() || null) : null,
      fanName: formData.fanName.trim() || null,
      fanSessionId,
      source: originSource
    };

    try {
      const response = await SubmitSuggestion(payload);

      if (response && response.success) {
        // Activar Cooldown
        const effectiveCdSeconds = response.data?.cooldownSeconds || config.cooldownSeconds || (cooldownMs / 1000);
        const cdMs = effectiveCdSeconds * 1000;
        const ts = Date.now() + cdMs;
        setNextAllowedTs(ts);
        try {
          localStorage.setItem(COOLDOWN_STORAGE_KEY, String(ts));
        } catch (storageErr) {
          console.error('[PlaylistFan] Error guardando cooldown:', storageErr);
        }

        // Guardar resultado para badge de votos
        setSubmissionResult(response.data || {});

        // Limpiar formulario
        setFormData({ title: '', artistName: '', fanName: '' });

        // Cambiar a vista de confirmación
        setView("sent");
        showMessage({
          type: MESSAGE_TYPE.GOOD,
          text: "¡Sugerencia enviada correctamente!"
        });
      } else {
        const errorText = response?.message || "No se pudo registrar la sugerencia. Intenta de nuevo.";
        setErrorMsg(errorText);
        showMessage({ type: MESSAGE_TYPE.BAD, text: errorText });
      }
    } catch (err) {
      console.error('[PlaylistFan] Error al enviar:', err);
      const errText = "Ocurrió un error inesperado al enviar tu sugerencia.";
      setErrorMsg(errText);
      showMessage({ type: MESSAGE_TYPE.BAD, text: errText });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="container SelfieFan PlaylistFan">

      {/* VISTA 1: INTRODUCCIÓN */}
      {view === "intro" && (
        <div className="SelfieFan__Intro PlaylistFan__Intro container pt-4">
          <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo Dorados FC" />
          <p className="PlaylistFan__Title">{config.screenTitle || "¡Arma la Playlist!"}</p>
          <p className="SelfieFan__Fan__Text--Center PlaylistFan__Text--Center">
            {config.screenSubtitle || "¿Qué canción o artista te gustaría escuchar en el estadio?"}
          </p>

          <ol className="SelfieFan__List PlaylistFan__List">
            <li>Elige si quieres proponer una canción o un artista.</li>
            <li>Escribe el nombre de tu preferencia.</li>
            <li>Da clic en “Enviar mi sugerencia”.</li>
          </ol>

          <p className="Messages__Fan__Text--Center PlaylistFan__Text--Center">
            Tu propuesta llegará directamente a la cabina de sonido del estadio El Encanto
          </p>

          <div className="SelfieFan__Intro__Actions PlaylistFan__Intro__Actions">
            <button
              className="Btn__Adsl--Red"
              onClick={handleStart}
              disabled={!canStartNew}
              title={!canStartNew ? `Espera ${msToMinSec(remainingMs)}` : ""}
            >
              {!canStartNew ? (
                `Puedes enviar otra en ${msToMinSec(remainingMs)}`
              ) : (
                <>
                  <i className="bi bi-music-note-beamed me-2"></i> Comenzar
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* VISTA 2: FORMULARIO DE CAPTURA */}
      {view === "form" && (
        <div className="Messages__Fan__FormContainer PlaylistFan__FormContainer pt-4">
          <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo Dorados FC" />

          <p className="Messages__Fan__Text--Title PlaylistFan__Text--Title PlaylistFan__Text--Center PlaylistFan__Element--SpaceUp">
            Propón tu música favorita
          </p>

          {/* Selector de Intención: Canción vs Artista */}
          <div className="PlaylistFan__SelectorContainer PlaylistFan__Element--SpaceUp">
            <button
              type="button"
              className={`Btn__Adsl--Selector ${suggestionType === 0 ? 'Btn__Adsl--Selector--Active' : ''}`}
              onClick={() => handleSelectorChange(0)}
              disabled={isSending}
            >
              <i className="bi bi-music-note-beamed"></i> Sugerir Canción
            </button>
            <button
              type="button"
              className={`Btn__Adsl--Selector ${suggestionType === 1 ? 'Btn__Adsl--Selector--Active' : ''}`}
              onClick={() => handleSelectorChange(1)}
              disabled={isSending}
            >
              <i className="bi bi-person-fill"></i> Sugerir Artista
            </button>
          </div>

          <form className="Messages__Fan__FormContainer__Form PlaylistFan__FormContainer__Form" onSubmit={handleSubmit}>
            {/* Formulario A: Sugerir Canción */}
            {suggestionType === 0 ? (
              <>
                <label className="Messages__Fan__FormContainer__Form--SpaceUp PlaylistFan__FormContainer__Form--SpaceUp">
                  Título de la canción *
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Ej. El Son de la Negra, La Chona..."
                  maxLength={100}
                  disabled={isSending}
                />
                <p className="text-end small text-muted mb-1">{formData.title.length}/100</p>

                <label className="Messages__Fan__FormContainer__Form--SpaceUp PlaylistFan__FormContainer__Form--SpaceUp">
                  Artista o Banda (opcional)
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="artistName"
                  value={formData.artistName}
                  onChange={handleChange}
                  placeholder="Ej. Los Tucanes de Tijuana, Coldplay..."
                  maxLength={80}
                  disabled={isSending}
                />
                <p className="text-end small text-muted mb-1">{formData.artistName.length}/80</p>
              </>
            ) : (
              /* Formulario B: Sugerir Artista */
              <>
                <label className="Messages__Fan__FormContainer__Form--SpaceUp PlaylistFan__FormContainer__Form--SpaceUp">
                  Artista o Grupo musical *
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Ej. Los Tigres del Norte, Coldplay, Grupo Frontera..."
                  maxLength={100}
                  disabled={isSending}
                />
                <p className="text-end small text-muted mb-1">{formData.title.length}/100</p>
              </>
            )}

            {/* Campo Remitente (Común a ambos) */}
            <label className="Messages__Fan__FormContainer__Form--SpaceUp PlaylistFan__FormContainer__Form--SpaceUp">
              ¿Quién sugiere? (opcional)
            </label>
            <input
              type="text"
              className="form-control"
              name="fanName"
              value={formData.fanName}
              onChange={handleChange}
              placeholder="Ej. Juan de Zona Sur"
              maxLength={50}
              disabled={isSending}
            />
            <p className="text-end small text-muted mb-1">{formData.fanName.length}/50</p>

            {/* Botones de Acción */}
            <button
              type="submit"
              className="Btn__Adsl--Red Messages__Fan__FormContainer__Form--SpaceUp PlaylistFan__FormContainer__Form--SpaceUp"
              disabled={isSending || !formData.title.trim()}
            >
              {isSending ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Enviando...
                </>
              ) : (
                <>
                  <i className="bi bi-send-fill me-2"></i> Enviar mi sugerencia
                </>
              )}
            </button>

            <button
              type="button"
              className="Btn__Adsl--RedOutline Messages__Fan__FormContainer__Form--SpaceUp PlaylistFan__FormContainer__Form--SpaceUp"
              onClick={() => setView("intro")}
              disabled={isSending}
            >
              Volver
            </button>

            {/* Mensaje de Error */}
            {errorMsg && (
              <p className="Messages__Fan__Text--Center PlaylistFan__Text--Center text-danger mt-3 fw-bold">
                {errorMsg}
              </p>
            )}
          </form>
        </div>
      )}

      {/* VISTA 3: CONFIRMACIÓN Y COOLDOWN (SENT) */}
      {view === "sent" && (
        <div className="SelfieFan__Sent PlaylistFan__Sent container">
          <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo Dorados FC" />
          <p className="fs-1 mb-0 text-success">
            <i className="bi bi-check-circle-fill"></i>
          </p>
          <p className="SelfieFan__Sent__Title PlaylistFan__Sent__Title">¡Sugerencia enviada!</p>
          <p className="Messages__Fan__Text--Center PlaylistFan__Text--Center">
            Tu propuesta ha sido enviada a la cabina de sonido
          </p>

          {/* Badge de Popularidad si ya suma votos previos */}
          {(submissionResult?.isGrouped || (submissionResult?.voteCount && submissionResult.voteCount > 1)) && (
            <div className="PlaylistFan__VoteBadge">
              🔥 ¡Esta canción ya suma {submissionResult.voteCount} votos de la afición!
            </div>
          )}

          <div className="SelfieFan__Sent__Actions PlaylistFan__Sent__Actions justify-content-center">
            <button
              className="Btn__Adsl--RedOutline"
              onClick={() => canStartNew && setView("intro")}
              disabled={!canStartNew}
            >
              {canStartNew ? (
                <>
                  <i className="bi bi-music-note-beamed me-2"></i> Enviar otra sugerencia
                </>
              ) : (
                `Puedes enviar otra en ${msToMinSec(remainingMs)}`
              )}
            </button>
          </div>

          <hr />

          <a
            href="https://interaktia.com/?utm_source=messages_web&utm_medium=banner&utm_campaign=fcm_doradosfc"
            target="_blank"
            rel="noreferrer"
            className="SelfieFan__Sent__Banner PlaylistFan__Sent__Banner"
          >
            <img
              src="/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif"
              alt="Publicidad Interaktia"
              className="d-block m-auto SelfieFan__Sent__Banner__Image PlaylistFan__Sent__Banner__Image"
            />
          </a>

          <a
            href="https://interaktia.com/?utm_source=messages_web&utm_medium=banner&utm_campaign=fcm_doradosfc"
            target="_blank"
            rel="noreferrer"
            className="Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block"
          >
            Contactar ahora
          </a>

          <hr />

          <p className="text-center mb-0">¿Quieres conocer más de esta tecnología?</p>
          <p className="text-center mb-0">Interaktia está listo para ti</p>
          <a
            href="https://interaktia.com/?utm_source=messages_web&utm_medium=banner_text&utm_campaign=fcm_doradosfc"
            target="_blank"
            rel="noreferrer"
            className="Btn__Adsl--RedOutline m-auto text-decoration-none text-center"
          >
            Contactar ahora
          </a>
        </div>
      )}

      {/* VISTA 4: DINÁMICA PAUSADA */}
      {view === "paused" && (
        <div className="SelfieFan__Sent PlaylistFan__Paused container pt-4">
          <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo Dorados FC" />
          <p className="fs-1 mb-0 text-warning">
            <i className="bi bi-pause-circle-fill"></i>
          </p>
          <p className="SelfieFan__Sent__Title PlaylistFan__Title">Dinámica en Pausa</p>
          <p className="Messages__Fan__Text--Center PlaylistFan__Text--Center">
            {config.pausedMessage || "¡Gracias por participar! La playlist para este partido ha cerrado. ¡Disfruta el encuentro!"}
          </p>

          <hr />

          <a
            href="https://interaktia.com/?utm_source=messages_web&utm_medium=banner&utm_campaign=fcm_doradosfc"
            target="_blank"
            rel="noreferrer"
            className="SelfieFan__Sent__Banner PlaylistFan__Sent__Banner"
          >
            <img
              src="/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif"
              alt="Publicidad Interaktia"
              className="d-block m-auto SelfieFan__Sent__Banner__Image PlaylistFan__Sent__Banner__Image"
            />
          </a>

          <a
            href="https://interaktia.com/?utm_source=messages_web&utm_medium=banner&utm_campaign=fcm_doradosfc"
            target="_blank"
            rel="noreferrer"
            className="Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block"
          >
            Contactar ahora
          </a>
        </div>
      )}

      {/* FOOTER DE CRÉDITOS */}
      <footer className="text-center mt-3 FooterCredits">
        Hecho con ♥ por{' '}
        <a
          href="https://interaktia.com/?utm_source=messages_web&utm_medium=footer&utm_campaign=fcm_doradosfc"
          target="_blank"
          rel="noreferrer"
          className="text-dark"
        >
          Interaktia
        </a>{' '}
        | 2026
      </footer>
    </div>
  );
};

export { PlaylistFan };
export default PlaylistFan;
